import React, { Suspense } from 'react'
import { Route, Navigate } from 'react-router-dom'
import * as ReactIs from 'react-is'
import { ErrorBoundary } from 'components'
import { Outlet } from 'react-router-dom'
/**
 * Takes an Array of routes and builds the react-router-dom Route components
 * Since Routes doesn't allow components different than Route, we use this function to return all elements
 * and then render them in the App.jsx
 *
 * this function is called recursevly until all child routes are processed
 *
 * **the routes object can use the following keys:**
 * - name: {string} The name of the route, used for generating the route name map
 * - path: {string|null} The path of the route, used for generating the route name map, if used in a child route, the path will be appended to the parent path
 * - layoutRoute: {boolean} The layout route dont have a path, will render all childs inside the layout in an <Outled> component
 * - element: {React.Component} The component to render for the route
 * - layout: {React.Component} Component The layout component to be used for the route, element will be rendered inside this layout
 * - guard: {React.Component} Component The guard component to be used for the route, if the guard component returns false, the route will navigate to login if no user or 403 error page
 * - layoutProps: {Object} The _props to be passed to the layout component
 * - elementProps: {Object} The _props to be passed to the element component
 * - guardProps: {Object} The _props to be passed to the guard component
 * - childs: {Array} Array of child routes, child routes will have the same options as the main ones
 * - suspenceFallback: {React.Component} The fallback component to be used when the route is loading
 *
 * @param {Array} params.routes Array of routes
 * @param {String} params.parentPath Path of the parent route will be appended to the path of the child routes
 * @returns {React.ReactElement} returns nested Route components to asemble the routes
 * @description Builds the route components from the routes object
 */

function routeBuilder(params) {
  const { parentPath = '', routes = [], parentRouteName = '' } = params
  return routes.map((route, routeIndex) => {
    const {
      name = '',
      element: Component = React.Fragment,
      path = null,
      pathIndex = null,
      guard: Guard = React.Fragment,
      layout: Layout = React.Fragment,
      childs = [],
      layoutProps = {},
      guardProps = {},
      elementProps = {},
      index = false,
      suspenceFallback: SuspenseFallback = React.Fragment,
    } = route

    const parentRouteNameProp = Boolean(parentRouteName)
      ? parentRouteName
      : null
    elementProps[parentRouteName] = parentRouteNameProp
    if (!ReactIs.isFragment(<Layout />) && !layoutProps?.parentRouteName) {
      layoutProps.parentRouteName = parentRouteNameProp
    }

    const Element = () => (
      <Guard {...guardProps}>
        <Suspense fallback={<SuspenseFallback />}>
          <Layout {...layoutProps}>
            <ErrorBoundary>
              {ReactIs.isFragment(<Component />) ? (
                <Outlet />
              ) : (
                <Component {...elementProps} />
              )}
            </ErrorBoundary>
          </Layout>
        </Suspense>
      </Guard>
    )


    
    const childrenRoutes = routeBuilder({
      routes: childs,
      parentPath: Boolean(path) ? path : parentPath,
      parentRouteName: Boolean(name) ? name : parentRouteName,
    })

    return (
      <React.Fragment key={routeIndex}>
        <Route element={<Element />} path={path} index={pathIndex}>
          {childrenRoutes}
        </Route>
        {index === true && (
          <Route element={<Navigate to={path} replace={true} />} index />
        )}
      </React.Fragment>
    )
  })
}

export default routeBuilder
